import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { mapStatus } from "../hooks/useOrders";
import type { AdminOrder, Lang, OrderDisplayStatus } from "../types";

export interface AdminContextValue {
  adminOrders: AdminOrder[];
  setAdminOrders: React.Dispatch<React.SetStateAction<AdminOrder[]>>;
  adminTab: string;
  setAdminTab: React.Dispatch<React.SetStateAction<string>>;
  adminTrackingInputs: Record<string, string>;
  setAdminTrackingInputs: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  adminAdvanceStatus: (orderId: string, currentStatus: string) => Promise<void>;
  adminSetTracking: (orderId: string, trackingNum: string) => Promise<void>;
  adminCreateShipment: (orderId: string, courier: string, trackingNum: string) => Promise<void>;
  mapStatus: (s: string) => OrderDisplayStatus;
}

/** UserRole alias (avoid name collision). */
export type AdminUserRole = "admin" | "moderator" | "vendor" | "user";

const AdminContext = createContext<AdminContextValue | null>(null);

interface ProviderProps {
  userId: string | null | undefined;
  userRole: AdminUserRole | string;
  lang: Lang;
  toast: (m: string) => void;
  children: React.ReactNode;
}

export function AdminProvider({ userId, userRole, lang, toast, children }: ProviderProps) {
  const [adminOrders, setAdminOrders] = useState<AdminOrder[]>([]);
  const [adminTab, setAdminTab] = useState<string>("orders");
  const [adminTrackingInputs, setAdminTrackingInputs] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!userId || userRole !== "admin") return;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("orders")
        .select("*, order_items(product_id, quantity, price_per_item)")
        .order("created_at", { ascending: false });
      if (cancelled || error) {
        if (error) console.error("Admin orders fetch error:", error);
        return;
      }
      const userIds = [...new Set((data || []).map((o: any) => o.user_id))];
      const { data: profiles } = userIds.length
        ? await supabase
            .from("profiles")
            .select("user_id, first_name, last_name, email")
            .in("user_id", userIds as string[])
        : ({ data: [] as any[] } as { data: any[] });
      const profMap = Object.fromEntries((profiles || []).map((p: any) => [p.user_id, p]));
      setAdminOrders(
        (data || []).map((o: any) => {
          const pr = profMap[o.user_id];
          return {
            id: o.id,
            shortId: o.id.slice(0, 13).toUpperCase(),
            cust: pr
              ? `${pr.first_name || ""} ${pr.last_name || ""}`.trim() || pr.email || "—"
              : o.user_id.slice(0, 8),
            items: o.order_items || [],
            total: o.total_amount,
            status: o.status,
            tracking: o.tracking_number || "",
            date: o.order_date?.split("T")[0] || o.created_at?.split("T")[0],
          };
        }),
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [userId, userRole]);

  const adminAdvanceStatus = useCallback(
    async (orderId: string, currentStatus: string) => {
      const sts = ["pending", "paid", "preparing", "shipped", "delivered", "finished"];
      const i = sts.indexOf(currentStatus);
      if (i >= sts.length - 1) return;
      const next = sts[i + 1];
      const { error } = await supabase.from("orders").update({ status: next }).eq("id", orderId);
      if (error) {
        toast(`❌ Error: ${error.message}`);
        return;
      }
      setAdminOrders((p) => p.map((o) => (o.id === orderId ? { ...o, status: next } : o)));
      toast(`📦 ${orderId.slice(0, 8)}… → ${mapStatus(next)}`);
      if (next === "shipped") {
        try {
          const { data: orderRow } = await supabase
            .from("orders")
            .select("user_id,tracking_number")
            .eq("id", orderId)
            .maybeSingle();
          if (orderRow?.user_id) {
            const { data: profile } = await supabase
              .from("profiles")
              .select("email,first_name")
              .eq("user_id", orderRow.user_id)
              .maybeSingle();
            if (profile?.email) {
              supabase.functions
                .invoke("send-transactional-email", {
                  body: {
                    templateName: "shipping-update",
                    recipientEmail: profile.email,
                    idempotencyKey: `shipping-${orderId}`,
                    templateData: {
                      name: profile.first_name || undefined,
                      orderId: orderId.slice(0, 13).toUpperCase(),
                      trackingNumber: orderRow.tracking_number || undefined,
                      carrier: "NutriMart Express",
                    },
                  },
                })
                .catch((e: any) => console.error("shipping email error:", e));
            }
          }
        } catch (e) {
          console.error("shipping email lookup error:", e);
        }
      }
    },
    [toast],
  );

  const adminSetTracking = useCallback(
    async (orderId: string, trackingNum: string) => {
      const { error } = await supabase
        .from("orders")
        .update({ tracking_number: trackingNum })
        .eq("id", orderId);
      if (error) {
        toast(`❌ Error: ${error.message}`);
        return;
      }
      setAdminOrders((p) => p.map((o) => (o.id === orderId ? { ...o, tracking: trackingNum } : o)));
      toast(`📦 ${lang === "es" ? "Tracking actualizado" : "Tracking updated"}`);
    },
    [toast, lang],
  );

  const adminCreateShipment = useCallback(
    async (orderId: string, courier: string, trackingNum: string) => {
      const { error } = await supabase
        .from("shipments")
        .insert({
          order_id: orderId,
          courier,
          tracking_number: trackingNum,
          status: "pending",
        });
      if (error) {
        toast(`❌ Error: ${error.message}`);
        return;
      }
      await supabase
        .from("orders")
        .update({ tracking_number: trackingNum })
        .eq("id", orderId);
      setAdminOrders((p) => p.map((o) => (o.id === orderId ? { ...o, tracking: trackingNum } : o)));
      toast(`📦 ${lang === "es" ? "Envío creado" : "Shipment created"}`);
    },
    [toast, lang],
  );

  const value: AdminContextValue = {
    adminOrders, setAdminOrders,
    adminTab, setAdminTab,
    adminTrackingInputs, setAdminTrackingInputs,
    adminAdvanceStatus, adminSetTracking, adminCreateShipment,
    mapStatus,
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
}

export function useAdmin(): AdminContextValue {
  const v = useContext(AdminContext);
  if (!v) throw new Error("useAdmin must be used inside <AdminProvider>");
  return v;
}